import React, { useState, useEffect } from 'react'
import moment from 'moment';
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { TextValidator, ValidatorForm, SelectValidator } from 'react-material-ui-form-validator';
import MenuItem from "@mui/material/MenuItem";
import { getPartner } from "../../../../../lib/access.es6";

const CAUSE_OF_ADMISSION_OPTIONS = [
  "Illness",
  "Accident",
  "Surgery",
  "Pregnancy-related",
];

// Maps each selectable cause to its key in the Hospital Cash Benefit's own
// additional_details.waiting_periods (e.g. {illness: 90, surgery: 180, accident: 0, pregnancy: 270}) -
// unlike every other benefit type, Hospital Cash has a DIFFERENT waiting period per cause of admission
// rather than one flat waiting_period_in_days, so accident (typically 0 days) can be claimable well before
// e.g. surgery (typically 180 days) on the exact same benefit.
const CAUSE_TO_WAITING_PERIOD_KEY = {
  "Illness": "illness",
  "Accident": "accident",
  "Surgery": "surgery",
  "Pregnancy-related": "pregnancy",
};

const HospitalClaimDetails = (props) => {
  const classes = useStyle();

  const [dischargeDate, setDischargeDate] = useState('');
  const [numberOfDaysInHospital, setNumberOfDaysInHospital] = useState('');
  const [hospitalAttended, setHospitalAttended] = useState('');
  const [causeOfHospitilization, setCauseOfHospitilization] = useState('');

  // Admission Date is not independently collected here - it IS the event date
  // already captured (and verified against the policy) back in SelectPolicy's
  // "Select Event Date" step, whose own helper text calls out that for a
  // hospitalisation claim that date is the admission date. Mirrors
  // FuneralClaimDetails' locked "Date Of Death" field, which reads the same
  // props.eventDate for the same reason. Pre-fill it into the payload as soon
  // as it's available rather than waiting on a user edit, since the field is
  // locked and no onChange will ever fire for it.
  const admissionDate = props.eventDate;

  useEffect(() => {
    if (admissionDate) {
      props.setAdditionalState("admission_date", admissionDate)
    }
  }, [admissionDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Discharge must be after admission.
  const dischargeBeforeAdmission =
    dischargeDate && admissionDate &&
    moment(dischargeDate).isSameOrBefore(moment(admissionDate));



  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "dischargeDate") {
      setDischargeDate(value)
      props.setAdditionalState("discharge_date", value)
      props.setAdditionalState("product_code", props.eventData.product_code)
      props.setAdditionalState("policy_start_date", props.eventData.start_date)
    }

  }

  useEffect(() => {
    if (dischargeDate && admissionDate) {
      const days = daysInHospital(dischargeDate, admissionDate);
      setNumberOfDaysInHospital(days);
      props.setAdditionalState("number_of_days_in_hospital", days)
    }
  }, [dischargeDate, admissionDate]);

  const daysInHospital = (dischargeDate, admissionDate) =>{

    return moment(dischargeDate).diff(moment(admissionDate), 'days');

  }

  // HospiCash estimated-benefit calculator. Daily rate and max-per-event are
  // read from the selected HospiCash benefit on the quote (same convention as
  // the "HOSPITAL ADMISSION" per-night display in SelectMember). Display only —
  // the backend computes the authoritative benefit amount.
  const getHospiCashBenefit = () => {
    const payload = props.claimInitiationPayload || {};
    const benefits = payload.benefits || [];
    return (
      benefits.find(function (benefit) {
        return benefit.type === "Hospital Cash Benefit";
      }) || null
    );
  };

  const hospiCashBenefit = getHospiCashBenefit();

  // Unlike other benefit types, Hospital Cash's waiting period depends on the cause of admission itself
  // (e.g. accident is typically immediate, surgery typically 180 days) - these come from the benefit's own
  // additional_details.waiting_periods. The anchor date is the policy's start_date - not
  // additional_details.hcp_inception_date, which claims-administration-dcp never reads and which can
  // diverge sharply from start_date (it's stamped from the day the onboarding/amendment flow happened
  // to run, not the policy's actual paid-for cover start), producing a client-side preview that doesn't
  // match what the customer has been paying for.
  const hcpInceptionDate = props.eventData && props.eventData.start_date;

  const getCauseWaitingPeriodDays = (cause) => {
    const key = CAUSE_TO_WAITING_PERIOD_KEY[cause];
    const waitingPeriods = hospiCashBenefit &&
      hospiCashBenefit.additional_details &&
      hospiCashBenefit.additional_details.waiting_periods;
    if (!key || !waitingPeriods || waitingPeriods[key] == null) return null;
    return waitingPeriods[key];
  };

  const isCauseWaitingPeriodMet = (cause) => {
    const waitingDays = getCauseWaitingPeriodDays(cause);
    if (waitingDays == null || !hcpInceptionDate) return true;
    const daysSince = moment().diff(moment(hcpInceptionDate), 'days');
    return daysSince >= waitingDays;
  };

  const causeEligibleFromDate = (cause) => {
    const waitingDays = getCauseWaitingPeriodDays(cause);
    if (waitingDays == null || !hcpInceptionDate) return '';
    return moment(hcpInceptionDate)
      .add(waitingDays, 'days')
      .format('DD-MM-YYYY');
  };

  const getDailyRateInCents = () => {
    if (
      !hospiCashBenefit ||
      !hospiCashBenefit.cover_amount_in_cents ||
      !hospiCashBenefit.cover_usage_count
    ) {
      return 0;
    }
    return (
      hospiCashBenefit.cover_amount_in_cents /
      hospiCashBenefit.cover_usage_count
    );
  };

  // Per-event cap = 10 payable days (BRD §6 / underwriter Y4). Applied as a day
  // cap in getPayableDays below rather than a cents cap on
  // hospiCashBenefit.cover_amount_in_cents, which at this pre-claim stage is
  // the quote/policy's ANNUAL bucket (daily rate x 50 days), not the single-
  // event max - no claim_benefit record (which does carry the real per-event
  // cap) exists yet at claim initiation.
  const HCP_PER_EVENT_DAY_CAP = 10;

  // The first 2 days are a non-payable qualifying period (48h); HospiCash pays
  // from day 3 onward, i.e. (days in hospital − 2), then caps at whatever's
  // left of the 10-day per-event limit after any linked prior admission (see
  // getAlreadyUsedEventDays) - same order the underwriter applies (exclude
  // first, then cap), so a long admission still saturates at the remaining
  // payable days instead of displaying an uncapped count that doesn't match
  // the (correctly capped) estimated benefit next to it.
  const NON_PAYABLE_DAYS = 2;

  // Claim statuses whose admission never actually happened / doesn't count
  // against the event - everything else (Pending, Approved, Approved_Paid,
  // etc.) represents a real admission and must still count even if that
  // claim's own approval workflow isn't finished yet.
  const EXCLUDED_LINK_STATUSES = [
    "Discarded",
    "Rejected",
    "Rejected_Pending",
    "Cancelled",
  ];

  const RE_ADMISSION_WINDOW_DAYS = 10;

  // BRD §6.x Re-admission Linking: a discharge followed by a re-admission
  // within 10 days (any cause) is the same Event as the admission before it -
  // not a new one. Each discharge restarts its own 10-day countdown, so a
  // chain of several close-together admissions can all link together. This
  // walks that chain backward from the current admission date, in the same
  // dependant's Hospital Cash Benefit claim history, summing how many of the
  // 10 payable days the linked admission(s) already used - exactly what the
  // underwriter engine does server-side at invoice time (see paid_days in
  // benefitInvoices.js). Only display-time; if this can't be determined (no
  // claim history loaded yet, no admission date), the estimate falls back to
  // the un-linked flat 10-day cap rather than blocking the form.
  const getLinkedPriorAdmissions = () => {
    if (!admissionDate || !props.member || !props.member.guid) return [];
    const claims = props.customerClaimsDcp || [];

    const candidates = claims
      .filter(function (claim) {
        if (claim.dependant_guid !== props.member.guid) return false;
        if (EXCLUDED_LINK_STATUSES.indexOf(claim.status) !== -1) return false;
        const hasHospiCash = (claim.benefits || []).some(function (b) {
          return b.type === "Hospital Cash Benefit";
        });
        if (!hasHospiCash) return false;
        const details =
          (claim.claim_details && claim.claim_details.additional_details) ||
          {};
        return !!details.discharge_date;
      })
      .map(function (claim) {
        const details = claim.claim_details.additional_details;
        const days =
          Number(details.days_admitted || details.number_of_days_in_hospital) ||
          0;
        const paidDays = Number(details.paid_days) || Math.max(0, days - NON_PAYABLE_DAYS);
        return {
          admission_date: details.admission_date,
          discharge_date: details.discharge_date,
          paid_days: paidDays,
        };
      });

    const chain = [];
    let cursor = moment(admissionDate);
    const pool = candidates.slice();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let bestIndex = -1;
      let bestDischarge = null;
      for (let i = 0; i < pool.length; i++) {
        const discharge = moment(pool[i].discharge_date);
        const precedesCursor = discharge.isValid() && discharge.isBefore(cursor);
        const gapDays = precedesCursor ? cursor.diff(discharge, "days") : -1;
        const withinWindow = gapDays >= 0 && gapDays < RE_ADMISSION_WINDOW_DAYS;
        if (withinWindow && (!bestDischarge || discharge.isAfter(bestDischarge))) {
          bestDischarge = discharge;
          bestIndex = i;
        }
      }
      if (bestIndex === -1) break;
      const linked = pool[bestIndex];
      chain.push(linked);
      pool.splice(bestIndex, 1);
      cursor = moment(linked.admission_date);
      if (!cursor.isValid()) break;
    }
    return chain;
  };

  const getAlreadyUsedEventDays = () => {
    const used = getLinkedPriorAdmissions().reduce(function (sum, admission) {
      return sum + admission.paid_days;
    }, 0);
    return Math.min(used, HCP_PER_EVENT_DAY_CAP);
  };

  const isLinkedReAdmission = () => getAlreadyUsedEventDays() > 0;

  const getRemainingEventCapDays = () =>
    Math.max(0, HCP_PER_EVENT_DAY_CAP - getAlreadyUsedEventDays());

  const getPayableDays = () => {
    const days = Math.max(
      0,
      (Number(numberOfDaysInHospital) || 0) - NON_PAYABLE_DAYS
    );
    return Math.min(days, getRemainingEventCapDays());
  };

  const getEstimatedBenefitInCents = () => {
    return getPayableDays() * getDailyRateInCents();
  };

  const currencyCode = getPartner().country.currency_code;

  const formatAmount = (amountInCents) => {
    return currencyCode + " " + (amountInCents / 100).toLocaleString();
  };

  const showBenefitCalculator = getDailyRateInCents() > 0;

  // Pregnancy-related admission is not applicable to male members.
  const isMaleMember =
    props.member &&
    props.member.gender &&
    props.member.gender.toString().toLowerCase() === "male";

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.headingclaim}>
            <h4>Claim Information For Hospitilization</h4>
          </div>
        </Grid>
      </Grid>
      <ValidatorForm onSubmit={() => { }}>
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>

          <Grid item xs={12} md={3} sm={3}>
            <TextValidator
              id="admissionDate"
              label="Admission Date"
              name='admissionDate'
              value={admissionDate}
              disabled={true}
              fullWidth
              type='date'
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please enter admission date']}
              style={{ width: "100%" }}
            />
          </Grid>



          <Grid item xs={12} md={3} sm={3}>
            <TextValidator
              id="dischargeDate"
              label="Discharge Date "
              name='dischargeDate'
              value={dischargeDate}
              onChange={handleChange}
              fullWidth
              type='date'
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              style={{ width: "100%" }}
            />
          </Grid>

          <Grid item xs={12} md={3} sm={3}>
            <TextValidator
              id="numberOfDaysInHospital"
              style={{ width: "100%" }}
              label="Number Of Days In Hospital"
              name='numberOfDaysInHospital'
              value={numberOfDaysInHospital}
              onChange={handleChange}
              disabled
              variant="outlined"
              // Minimum admission of 48 hours (2 days) applies to all causes,
              // including accident, per the BRD. There is no maximum - days in
              // hospital must match the discharge form, and payout is capped
              // per event (see getEstimatedBenefitInCents), not by day count.
              validators={['required', 'minNumber:2']}
              errorMessages={[
                'This field is required',
                'Minimum admission is 48 hours (2 days)'
              ]}
            />
          </Grid>

          {dischargeBeforeAdmission && (
            <Grid item xs={12}>
              <p style={{ color: 'red', fontSize: '12px', margin: '0 8px' }}>
                Discharge date must be after the admission date.
              </p>
            </Grid>
          )}

          <Grid item xs={12} md={3} sm={3}>
            <TextValidator
              id="hospital_attended"
              style={{ width: "100%" }}
              label="Hospital Attended"
              name='hospital_attended'
              value={hospitalAttended}
              type='text'
              onChange={(e) => {
                setHospitalAttended(e.target.value)
                props.setAdditionalState("hospital_attended", e.target.value)
              }}
              variant="outlined"
          
            />
          </Grid>

          <Grid item xs={12} md={3} sm={3}>
            <SelectValidator
              id="cause_of_hospitilization"
              style={{ width: "100%" }}
              label="Cause Of Admission"
              name='cause_of_hospitilization'
              value={causeOfHospitilization}
              onChange={(e) => {
                setCauseOfHospitilization(e.target.value)
                props.setAdditionalState("cause_of_hospitilization", e.target.value)
              }}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select cause of admission']}
            >
              {CAUSE_OF_ADMISSION_OPTIONS.map((cause, index) => {
                const notApplicable = cause === "Pregnancy-related" && isMaleMember;
                const waitingPeriodMet = isCauseWaitingPeriodMet(cause);
                const disabled = notApplicable || !waitingPeriodMet;
                return (
                  <MenuItem key={index} value={cause} disabled={disabled}>
                    {cause}
                    {!notApplicable && !waitingPeriodMet && (
                      <> (waiting period not met, eligible from {causeEligibleFromDate(cause)})</>
                    )}
                  </MenuItem>
                );
              })}
            </SelectValidator>
          </Grid>


        </Grid>

        {showBenefitCalculator && (
          <Grid container spacing={2} className={classes.selectpolicyWrapper}>
            <Grid item xs={12} sm={12} md={12}>
              <div className={classes.headingclaim}>
                <h4>Estimated HospiCash Benefit</h4>
              </div>
            </Grid>
            <Grid item xs={12} md={4} sm={4}>
              <TextValidator
                id="estimated_daily_rate"
                style={{ width: "100%" }}
                label="Daily Rate"
                name="estimated_daily_rate"
                value={formatAmount(getDailyRateInCents())}
                disabled
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4} sm={4}>
              <TextValidator
                id="estimated_days"
                style={{ width: "100%" }}
                label="Payable Days (excl. first 2)"
                name="estimated_days"
                value={getPayableDays()}
                disabled
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4} sm={4}>
              <TextValidator
                id="estimated_benefit"
                style={{ width: "100%" }}
                label="Estimated Benefit (capped at max per event)"
                name="estimated_benefit"
                value={formatAmount(getEstimatedBenefitInCents())}
                disabled
                variant="outlined"
              />
            </Grid>
            {isLinkedReAdmission() && (
              <Grid item xs={12}>
                <p style={{ color: '#8a6d3b', fontSize: '12px', margin: '0 8px' }}>
                  Re-admission within 10 days is considered one event: {getAlreadyUsedEventDays()} of{' '}
                  {HCP_PER_EVENT_DAY_CAP} payable days for this event were already used by a linked
                  prior admission, leaving {getRemainingEventCapDays()} remaining.
                </p>
              </Grid>
            )}
          </Grid>
        )}


      </ValidatorForm>
    </>
  )
}

export default HospitalClaimDetails
